{
	"openapi": "3.1.0",
	"info": {
		"description": "The Whop REST API. Please see https://docs.whop.com/developer/api/getting-started for more details.",
		"termsOfService": "https://whop.com/tos-developer-api/",
		"title": "Whop API",
		"version": "1.0.0",
		"x-api-version-date": "2026-09-15"
	},
	"paths": {
		"/app_builds": {
			"get": {
				"description": "Returns a paginated list of build artifacts for an app, newest first, with optional platform, status, and creation-date filters.",
				"operationId": "list",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "The app to list builds for, prefixed `app_`.",
						"in": "query",
						"name": "app_id",
						"required": true,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Filter builds by target platform.",
						"in": "query",
						"name": "platform",
						"required": false,
						"schema": {
							"enum": ["ios", "android", "web"],
							"type": "string"
						}
					},
					{
						"description": "Filter builds by review status.",
						"in": "query",
						"name": "status",
						"required": false,
						"schema": {
							"enum": ["draft", "pending", "approved", "rejected"],
							"type": "string"
						}
					},
					{
						"description": "Only return builds created before this ISO 8601 timestamp.",
						"in": "query",
						"name": "created_before",
						"required": false,
						"schema": {
							"type": ["integer", "string"]
						}
					},
					{
						"description": "Only return builds created after this ISO 8601 timestamp.",
						"in": "query",
						"name": "created_after",
						"required": false,
						"schema": {
							"type": ["integer", "string"]
						}
					},
					{
						"description": "Number of results to return from the start of the range.",
						"in": "query",
						"name": "first",
						"required": false,
						"schema": {
							"default": 20,
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results after this cursor. Use `page_info.end_cursor` from the previous response to fetch the next page.",
						"in": "query",
						"name": "after",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the end of the range.",
						"in": "query",
						"name": "last",
						"required": false,
						"schema": {
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results before this cursor. Use `page_info.start_cursor` from the previous response to fetch the previous page.",
						"in": "query",
						"name": "before",
						"required": false,
						"schema": {
							"type": "string"
						}
					}
				],
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"properties": {
										"data": {
											"items": {
												"$ref": "#/components/schemas/AppBuild"
											},
											"type": "array"
										},
										"page_info": {
											"properties": {
												"end_cursor": {
													"type": ["string", "null"]
												},
												"has_next_page": {
													"example": false,
													"type": "boolean"
												},
												"has_previous_page": {
													"example": false,
													"type": "boolean"
												},
												"start_cursor": {
													"example": "WyJjdXJzb3IiLDFd",
													"type": ["string", "null"]
												}
											},
											"required": [
												"end_cursor",
												"start_cursor",
												"has_next_page",
												"has_previous_page"
											],
											"type": "object"
										}
									},
									"required": ["data", "page_info"],
									"type": "object"
								}
							}
						},
						"description": "app builds listed"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "the app_id is missing"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the build-management scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app with that ID exists"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:manage_builds"]
					}
				],
				"summary": "List App Builds",
				"tags": ["App Builds"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "List"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.appBuilds.list({\n    app_id: \"app_id\"\n});\n"
					}
				]
			}
		},
		"/app_builds/{id}": {
			"get": {
				"description": "Retrieves the details of an existing app build.",
				"operationId": "get",
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/AppBuild"
								}
							}
						},
						"description": "app build retrieved"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the build-management scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app build with that ID exists"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:manage_builds"]
					}
				],
				"summary": "Retrieve App Build",
				"tags": ["App Builds"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Retrieve"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.appBuilds.retrieve({\n    id: \"id\"\n});\n"
					}
				],
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App build ID, prefixed `abld_`.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				]
			}
		},
		"/app_builds/{id}/promote": {
			"post": {
				"description": "Promotes a draft or approved app build to production so it becomes the active version served to users. Draft builds enter review first.",
				"operationId": "promote",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App build ID, prefixed `abld_`.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					},
					{
						"$ref": "#/components/parameters/IdempotencyKey"
					}
				],
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/AppBuild"
								}
							}
						},
						"description": "app build promotion started"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "the build cannot be promoted"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the build-management scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app build with that ID exists"
					},
					"409": {
						"$ref": "#/components/responses/Conflict"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:manage_builds"]
					}
				],
				"summary": "Promote App Build",
				"tags": ["App Builds"],
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.appBuilds.promote({\n    id: \"id\"\n});\n"
					}
				]
			}
		},
		"/apps": {
			"get": {
				"description": "Lists apps on the Whop platform: the app store's live apps, or — with `account_id` and developer access to that account — every app the account owns. Requires authentication except for Whop's public app and website discovery lists. Public website discovery includes built official blueprints (verified apps with a product) and built, live community blueprints that Whop recommends.",
				"operationId": "list",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "Only return apps created by this account (`biz_` tag). With developer access to the account this includes its unlisted and hidden apps.",
						"in": "query",
						"name": "account_id",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Filter apps by the type of end-user they are built for. Apps of type `website` are left out unless you ask for them by name.",
						"in": "query",
						"name": "app_type",
						"required": false,
						"schema": {
							"enum": [
								"b2b_app",
								"b2c_app",
								"company_app",
								"component",
								"website"
							],
							"type": "string"
						}
					},
					{
						"description": "Only return apps supporting this view type, such as `dashboard` or `hub`.",
						"in": "query",
						"name": "view_type",
						"required": false,
						"schema": {
							"enum": [
								"hub",
								"discover",
								"dash",
								"dashboard",
								"analytics",
								"skills",
								"openapi"
							],
							"type": "string"
						}
					},
					{
						"description": "Only return apps whose Whop verification status matches this value. Omit this filter to include every verification status the caller can see.",
						"in": "query",
						"name": "verified",
						"required": false,
						"schema": {
							"type": "boolean"
						}
					},
					{
						"deprecated": true,
						"description": "Legacy compatibility filter. Use `verified` for field equality. `true` returns verified apps; clients pinned before `2026-08-25-2` retain the earlier public website discovery behavior.",
						"in": "query",
						"name": "verified_apps_only",
						"required": false,
						"schema": {
							"type": "boolean"
						}
					},
					{
						"description": "Only return apps Whop recommends (or, with `false`, only those it does not), independently of verification status.",
						"in": "query",
						"name": "recommended",
						"required": false,
						"schema": {
							"type": "boolean"
						}
					},
					{
						"description": "A search string matched against app names.",
						"in": "query",
						"name": "query",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "The field to sort apps by. Defaults to discoverable_at, showing the most recently published apps first. `template_usage` ranks Whop-verified apps first, then by how many businesses created apps from each app as a template.",
						"in": "query",
						"name": "order",
						"required": false,
						"schema": {
							"default": "discoverable_at",
							"enum": [
								"created_at",
								"discoverable_at",
								"template_usage",
								"total_installs_last_30_days",
								"total_installs_last_7_days"
							],
							"type": "string"
						}
					},
					{
						"description": "Sort direction.",
						"in": "query",
						"name": "direction",
						"required": false,
						"schema": {
							"default": "desc",
							"enum": ["asc", "desc"],
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the start of the range.",
						"in": "query",
						"name": "first",
						"required": false,
						"schema": {
							"default": 20,
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results after this cursor. Use `page_info.end_cursor` from the previous response to fetch the next page.",
						"in": "query",
						"name": "after",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the end of the range.",
						"in": "query",
						"name": "last",
						"required": false,
						"schema": {
							"maximum": 100,
							"type": "integer"
						}
					},
					{
						"description": "Return results before this cursor. Use `page_info.start_cursor` from the previous response to fetch the previous page.",
						"in": "query",
						"name": "before",
						"required": false,
						"schema": {
							"type": "string"
						}
					}
				],
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"properties": {
										"data": {
											"items": {
												"$ref": "#/components/schemas/AppListItem"
											},
											"type": "array"
										},
										"page_info": {
											"properties": {
												"end_cursor": {
													"example": "Mg",
													"type": ["string", "null"]
												},
												"has_next_page": {
													"example": false,
													"type": "boolean"
												},
												"has_previous_page": {
													"example": false,
													"type": "boolean"
												},
												"start_cursor": {
													"example": "WyJjdXJzb3IiLDFd",
													"type": ["string", "null"]
												}
											},
											"required": [
												"end_cursor",
												"start_cursor",
												"has_next_page",
												"has_previous_page"
											],
											"type": "object"
										}
									},
									"required": ["data", "page_info"],
									"type": "object"
								}
							}
						},
						"description": "apps listed"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "invalid list parameters"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					}
				},
				"security": [
					{
						"bearerAuth": []
					}
				],
				"summary": "List Apps",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "List"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.list();\n"
					}
				]
			},
			"post": {
				"description": "Registers a new app on the Whop developer platform. Apps provide custom experiences that can be added to products.",
				"operationId": "create",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"$ref": "#/components/parameters/IdempotencyKey"
					}
				],
				"requestBody": {
					"content": {
						"application/json": {
							"schema": {
								"properties": {
									"account_id": {
										"description": "The account to create the app for (`biz_` tag). Defaults to the account behind the presented credential.",
										"example": "biz_xxxxxxxxxxxxxx",
										"type": "string"
									},
									"app_type": {
										"default": "b2c_app",
										"description": "The type of app to create. Defaults to `b2c_app`.",
										"enum": [
											"b2b_app",
											"b2c_app",
											"company_app",
											"component",
											"website"
										],
										"example": "website",
										"type": "string"
									},
									"base_url": {
										"description": "The base production URL where the app is hosted, such as `https://myapp.example.com`.",
										"example": "https://booking.shinetime.example",
										"type": ["string", "null"]
									},
									"icon": {
										"description": "The icon image for the app in PNG, JPEG, or GIF format, referencing an uploaded file: `{ id }` for an existing attachment or `{ direct_upload_id }` for a new direct upload.",
										"properties": {
											"direct_upload_id": {
												"description": "The signed id of a completed direct upload.",
												"example": "eyJfcmFpbHMiOnsiZGF0YSI6MSwicHVyIjoiYmxvYl9pZCJ9fQ==--xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
												"type": "string"
											},
											"id": {
												"description": "The tag of an already-uploaded attachment.",
												"example": "file_xxxxxxxxxxxxxx",
												"type": "string"
											}
										},
										"type": "object"
									},
									"name": {
										"description": "The display name for the app, shown to users on the app store and product pages.",
										"example": "Shine Time Booking",
										"type": "string"
									},
									"redirect_uris": {
										"description": "The whitelisted OAuth callback URLs that users are redirected to after authorizing the app.",
										"items": {
											"example": "https://booking.shinetime.example/oauth/callback",
											"type": "string"
										},
										"type": "array"
									},
									"route": {
										"description": "The subdomain route where the app's hosted web builds are served, such as `myapp` for myapp.whop.site.",
										"example": "shine-time-booking-site",
										"type": ["string", "null"]
									}
								},
								"required": ["name"],
								"type": "object"
							}
						}
					},
					"required": true
				},
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/App"
								}
							}
						},
						"description": "app created"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "the request body is invalid"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the app-creation scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no account with that ID exists"
					},
					"409": {
						"$ref": "#/components/responses/Conflict"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:create_app"]
					}
				],
				"summary": "Create App",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Create"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.create({\n    name: \"Shine Time Booking\"\n});\n"
					}
				]
			}
		},
		"/apps/{id}": {
			"delete": {
				"description": "Deletes an app. The app stops resolving within seconds — a website's site stops serving, and any claimed subdomain is reserved for a month before it can be claimed again.",
				"operationId": "delete",
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"properties": {
										"deleted": {
											"description": "Always true.",
											"example": true,
											"type": "boolean"
										},
										"id": {
											"description": "ID of the deleted app.",
											"example": "app_xxxxxxxxxxxxxx",
											"type": "string"
										}
									},
									"required": ["id", "deleted"],
									"type": "object"
								}
							}
						},
						"description": "app deleted"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the app-update scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app with that ID exists"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:update_app"]
					}
				],
				"summary": "Delete App",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Delete"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.delete({\n    id: \"id\"\n});\n"
					}
				],
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App ID (prefixed `app_`). Retrieval also accepts the app's claimed route, an active verified custom hostname, or its proxy domain id.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				]
			},
			"get": {
				"description": "Retrieves an app by ID, claimed route, active verified custom hostname, or proxy domain id. Custom hostnames return 404 for inactive assignments, suspended accounts, or deleted apps. Credential fields (api_key, default_api_key, secrets) render `null` unless the caller has the corresponding developer permission on the owning account.",
				"operationId": "get",
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/App"
								}
							}
						},
						"description": "app retrieved"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app with that ID exists"
					}
				},
				"security": [
					{},
					{
						"bearerAuth": []
					}
				],
				"summary": "Retrieve App",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Retrieve"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.retrieve({\n    id: \"id\"\n});\n"
					}
				],
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App ID (prefixed `app_`). Retrieval also accepts the app's claimed route, an active verified custom hostname, or its proxy domain id.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				]
			},
			"patch": {
				"description": "Updates the settings, metadata, or status of an app. Fields that are omitted keep their current value.",
				"operationId": "update",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App ID (prefixed `app_`). Retrieval also accepts the app's claimed route, an active verified custom hostname, or its proxy domain id.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				],
				"requestBody": {
					"content": {
						"application/json": {
							"schema": {
								"properties": {
									"app_store_description": {
										"description": "The detailed description shown on the app store's in-depth app view page.",
										"example": "Shine Time Booking turns a whop into a booking calendar. Members pick a package, choose a slot that fits the day's route, and pay up front.",
										"type": "string"
									},
									"app_type": {
										"description": "The type of end-user the app is built for. Cannot be changed on an app whose type is already `website`.",
										"enum": [
											"b2b_app",
											"b2c_app",
											"company_app",
											"component",
											"website"
										],
										"example": "b2c_app",
										"type": "string"
									},
									"base_url": {
										"description": "The base production URL where the app is hosted. Set to `null` to take the app proxy offline.",
										"example": "https://booking.shinetime.example",
										"type": ["string", "null"]
									},
									"dashboard_path": {
										"description": "The URL path for the account dashboard view.",
										"example": "/dashboard/[companyId]",
										"type": ["string", "null"]
									},
									"description": {
										"description": "A short description of the app shown in listings and search results.",
										"example": "Let members book a mobile detailing appointment without leaving your whop.",
										"type": "string"
									},
									"discover_path": {
										"description": "The URL path for the discover view.",
										"example": "/discover",
										"type": ["string", "null"]
									},
									"experience_path": {
										"description": "The URL path for the member-facing hub view, such as `/experiences/[experienceId]`.",
										"example": "/experiences/[experienceId]",
										"type": ["string", "null"]
									},
									"icon": {
										"description": "The icon image for the app in PNG, JPEG, or GIF format, referencing an uploaded file: `{ id }` for an existing attachment or `{ direct_upload_id }` for a new direct upload.",
										"properties": {
											"direct_upload_id": {
												"description": "The signed id of a completed direct upload.",
												"example": "eyJfcmFpbHMiOnsiZGF0YSI6MSwicHVyIjoiYmxvYl9pZCJ9fQ==--xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
												"type": "string"
											},
											"id": {
												"description": "The tag of an already-uploaded attachment.",
												"example": "file_xxxxxxxxxxxxxx",
												"type": "string"
											}
										},
										"type": "object"
									},
									"name": {
										"description": "The display name for the app, shown to users on the app store and product pages.",
										"example": "Shine Time Booking Pro",
										"type": "string"
									},
									"oauth_client_type": {
										"description": "How the app authenticates at the OAuth token endpoint.",
										"enum": ["public", "confidential"],
										"example": "confidential",
										"type": "string"
									},
									"openapi_path": {
										"description": "The URL path to the app's OpenAPI spec file (requires the ai_chat capability).",
										"example": "/openapi.json",
										"type": ["string", "null"]
									},
									"production_android_build_id": {
										"description": "The app build (`abld_` tag) to serve as the Android production build, or `null` to unassign it. Same rules as `production_web_build_id`.",
										"example": "apbu_xxxxxxxxxxxxxx",
										"type": ["string", "null"]
									},
									"production_ios_build_id": {
										"description": "The app build (`abld_` tag) to serve as the iOS production build, or `null` to unassign it. Same rules as `production_web_build_id`.",
										"example": "apbu_xxxxxxxxxxxxxx",
										"type": ["string", "null"]
									},
									"production_web_build_id": {
										"description": "The app build (`abld_` tag) to serve as the web production build, or `null` to unassign it. The build must belong to this app, target web, and be in the draft or approved status; a draft build is queued for approval and takes over once approved. Requires the `developer:manage_builds` scope.",
										"example": "apbu_xxxxxxxxxxxxxx",
										"type": ["string", "null"]
									},
									"redirect_uris": {
										"description": "The whitelisted OAuth callback URLs users are redirected to after authorizing the app.",
										"items": {
											"example": "https://booking.shinetime.example/oauth/callback",
											"type": "string"
										},
										"type": "array"
									},
									"required_scopes": {
										"description": "The OAuth scopes the app requests from users when they install it.",
										"items": {
											"example": "read_user",
											"type": "string"
										},
										"type": "array"
									},
									"route": {
										"description": "The subdomain route where the app's hosted web builds are served.",
										"example": "shine-time-booking-pro",
										"type": "string"
									},
									"secrets": {
										"description": "Secrets to add or overwrite on the app, as an object of string values. Keys not included are left untouched; pass null or an empty string as the value to delete a secret. Encrypted at rest and injected into the app's hosted server runtime.",
										"example": {
											"BOOKING_CALENDAR_ID": "cal_9f21"
										},
										"type": "object"
									},
									"skills_path": {
										"description": "The URL path to the app's skills directory (requires the ai_chat capability).",
										"example": "/skills",
										"type": ["string", "null"]
									},
									"status": {
										"description": "Controls whether the app is published on Whop discovery or accessible only through its direct link. Publishing requires a name, icon, and description.",
										"enum": ["live", "unlisted", "hidden"],
										"example": "unlisted",
										"type": "string"
									}
								},
								"type": "object"
							}
						}
					},
					"required": true
				},
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/App"
								}
							}
						},
						"description": "app updated"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "the request body is invalid"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the app-update scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app with that ID exists"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:update_app"]
					}
				],
				"summary": "Update App",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Update"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.update({\n    id: \"id\"\n});\n"
					}
				]
			}
		},
		"/apps/{id}/logs": {
			"get": {
				"description": "Lists a hosted app's server runtime logs, most recent first: console output, uncaught exceptions, and failed-request summaries captured on whop.site hosting. Logs are retained for 7 days.",
				"operationId": "logs",
				"parameters": [
					{
						"description": "The ID of the app, which will look like app_*************.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Only return logs from this build.",
						"in": "query",
						"name": "app_build_id",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Only return console lines of this level.",
						"in": "query",
						"name": "level",
						"required": false,
						"schema": {
							"enum": ["log", "debug", "info", "warn", "error"],
							"type": "string"
						}
					},
					{
						"description": "Only return logs whose message contains this text (case-insensitive).",
						"in": "query",
						"name": "query",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Start of the time window as an ISO 8601 timestamp. Defaults to 7 days before created_before.",
						"in": "query",
						"name": "created_after",
						"required": false,
						"schema": {
							"format": "date-time",
							"type": "string"
						}
					},
					{
						"description": "End of the time window as an ISO 8601 timestamp. Defaults to now.",
						"in": "query",
						"name": "created_before",
						"required": false,
						"schema": {
							"format": "date-time",
							"type": "string"
						}
					},
					{
						"description": "Number of results to return from the start of the range.",
						"in": "query",
						"name": "first",
						"required": false,
						"schema": {
							"default": 100,
							"maximum": 500,
							"type": "integer"
						}
					},
					{
						"description": "Return results after this cursor. Use `page_info.end_cursor` from the previous response to fetch the next page.",
						"in": "query",
						"name": "after",
						"required": false,
						"schema": {
							"type": "string"
						}
					},
					{
						"description": "Return results before this cursor. Use `page_info.start_cursor` from the previous response to fetch the previous page.",
						"in": "query",
						"name": "before",
						"required": false,
						"schema": {
							"type": "string"
						}
					}
				],
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"properties": {
										"data": {
											"items": {
												"properties": {
													"app_build_id": {
														"example": "abld_123",
														"type": "string"
													},
													"app_id": {
														"example": "app_xxxxxxxxxxxxxx",
														"type": "string"
													},
													"cpu_time_ms": {
														"example": 8,
														"type": "integer"
													},
													"created_at": {
														"example": "2026-01-01T12:00:00.000Z",
														"format": "date-time",
														"type": "string"
													},
													"level": {
														"enum": ["log", "debug", "info", "warn", "error"],
														"example": "error",
														"type": "string"
													},
													"message": {
														"example": "TypeError: Cannot read properties of undefined (reading 'slotId')",
														"type": "string"
													},
													"outcome": {
														"example": "exception",
														"type": ["string", "null"]
													},
													"request_id": {
														"example": "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
														"type": "string"
													},
													"request_method": {
														"example": "GET",
														"type": ["string", "null"]
													},
													"request_path": {
														"example": "/api/slots",
														"type": ["string", "null"]
													},
													"response_status": {
														"example": 500,
														"type": ["integer", "null"]
													},
													"source": {
														"enum": ["console", "exception", "request"],
														"example": "exception",
														"type": "string"
													},
													"stack": {
														"example": "TypeError: Cannot read properties of undefined (reading 'slotId')\n    at renderBookingSlot (/worker/booking.js:118:24)\n    at handleRequest (/worker/index.js:42:11)",
														"type": ["string", "null"]
													},
													"truncated": {
														"example": false,
														"type": "boolean"
													},
													"wall_time_ms": {
														"example": 31,
														"type": "integer"
													}
												},
												"required": [
													"app_id",
													"app_build_id",
													"request_id",
													"created_at",
													"source",
													"level",
													"message"
												],
												"type": "object"
											},
											"type": "array"
										},
										"page_info": {
											"properties": {
												"end_cursor": {
													"example": "cursor_end",
													"type": ["string", "null"]
												},
												"has_next_page": {
													"example": false,
													"type": "boolean"
												},
												"has_previous_page": {
													"example": false,
													"type": "boolean"
												},
												"start_cursor": {
													"example": "cursor_start",
													"type": ["string", "null"]
												}
											},
											"required": ["has_next_page", "has_previous_page"],
											"type": "object"
										}
									},
									"required": ["data", "page_info"],
									"type": "object"
								}
							}
						},
						"description": "logs listed"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "missing the developer:logs:read scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "app not found"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:logs:read"]
					}
				],
				"summary": "List App Logs",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "List Logs"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.logs({\n    id: \"id\"\n});\n"
					}
				]
			}
		},
		"/apps/{id}/permissions": {
			"patch": {
				"description": "Replaces the set of permissions the app requests from users when they install it. Requires a user session: the `developer:update_app_authorization` scope cannot be delegated to API keys. Sensitive permissions require step-up verification.",
				"operationId": "permissions",
				"parameters": [
					{
						"$ref": "#/components/parameters/ApiVersionDate"
					},
					{
						"description": "App ID, prefixed `app_`.",
						"in": "path",
						"name": "id",
						"required": true,
						"schema": {
							"type": "string"
						}
					}
				],
				"requestBody": {
					"content": {
						"application/json": {
							"schema": {
								"properties": {
									"requested_permissions": {
										"description": "The full set of permissions the app requests on install; permissions not listed are removed.",
										"items": {
											"properties": {
												"action": {
													"description": "The permission action, for example `company:basic:read`.",
													"example": "company:basic:read",
													"type": "string"
												},
												"is_required": {
													"description": "Whether installing the app requires granting this permission.",
													"example": true,
													"type": "boolean"
												},
												"justification": {
													"description": "Why the app needs this permission (20-512 characters), shown to the installing user.",
													"example": "Reads basic account info to render the dashboard home.",
													"type": "string"
												}
											},
											"required": ["action", "is_required", "justification"],
											"type": "object"
										},
										"type": "array"
									}
								},
								"required": ["requested_permissions"],
								"type": "object"
							}
						}
					},
					"required": true
				},
				"responses": {
					"200": {
						"content": {
							"application/json": {
								"schema": {
									"$ref": "#/components/schemas/App"
								}
							}
						},
						"description": "requested permissions updated"
					},
					"400": {
						"$ref": "#/components/responses/InvalidParameters",
						"description": "the request body is invalid"
					},
					"401": {
						"$ref": "#/components/responses/Unauthorized",
						"description": "missing or invalid authentication"
					},
					"403": {
						"$ref": "#/components/responses/Forbidden",
						"description": "credential lacks the app-authorization scope"
					},
					"404": {
						"$ref": "#/components/responses/NotFound",
						"description": "no app with that ID exists"
					}
				},
				"security": [
					{
						"bearerAuth": ["developer:update_app_authorization"]
					}
				],
				"summary": "Update App Permissions",
				"tags": ["Apps"],
				"x-mint": {
					"metadata": {
						"sidebarTitle": "Update Permissions"
					}
				},
				"x-codeSamples": [
					{
						"lang": "TypeScript",
						"source": "import { WhopClient } from \"@whop/sdk\";\n\nconst client = new WhopClient({ token: \"YOUR_TOKEN\", apiVersionDate: \"2026-08-21-1\", idempotencyKey: \"YOUR_IDEMPOTENCY_KEY\" });\nawait client.apps.updatePermissions({\n    id: \"id\",\n    requested_permissions: [{\n            action: \"company:basic:read\",\n            is_required: true,\n            justification: \"Reads basic account info to render the dashboard home.\"\n        }]\n});\n"
					}
				]
			}
		}
	},
	"components": {
		"schemas": {
			"AppBuild": {
				"properties": {
					"checksum": {
						"description": "Client-generated checksum of the build file, used to verify file integrity.",
						"example": "xxxxxxxxxxxxxxx",
						"type": "string"
					},
					"created_at": {
						"description": "When the build was uploaded, as an ISO 8601 timestamp.",
						"example": "2026-01-01T12:00:00.000Z",
						"type": "string"
					},
					"file_url": {
						"description": "URL to download the uploaded build artifact.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"id": {
						"description": "App build ID, prefixed `abld_`.",
						"example": "apbu_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"is_production": {
						"description": "Whether this build is the currently active production build for its platform.",
						"example": false,
						"type": "boolean"
					},
					"platform": {
						"description": "The target platform for this build.",
						"enum": ["ios", "android", "web"],
						"example": "ios",
						"type": "string"
					},
					"review_message": {
						"description": "Feedback from the reviewer explaining a rejection, or `null` if the build has not been reviewed or was approved.",
						"example": "The hub view crashes on launch when the member has no active membership. Fix and resubmit.",
						"type": ["string", "null"]
					},
					"source_url": {
						"description": "URL to download the compressed source code archive that produced this build, or `null` when the build was uploaded without a source archive.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"status": {
						"description": "The build's review status.",
						"enum": ["draft", "pending", "approved", "rejected"],
						"example": "rejected",
						"type": "string"
					},
					"supported_app_view_types": {
						"items": {
							"description": "The view types this build supports, as declared by the developer.",
							"enum": [
								"hub",
								"discover",
								"dash",
								"dashboard",
								"analytics",
								"skills",
								"openapi"
							],
							"example": "hub",
							"type": "string"
						},
						"type": "array"
					}
				},
				"required": [
					"id",
					"platform",
					"file_url",
					"source_url",
					"created_at",
					"status",
					"checksum",
					"supported_app_view_types",
					"review_message",
					"is_production"
				],
				"type": "object"
			},
			"AppListItem": {
				"properties": {
					"account": {
						"$ref": "#/components/schemas/AccountParent",
						"description": "The account that owns the app."
					},
					"app_type": {
						"description": "The type of end-user the app is built for.",
						"enum": [
							"b2b_app",
							"b2c_app",
							"company_app",
							"component",
							"website"
						],
						"example": "b2c_app",
						"type": "string"
					},
					"banner_image": {
						"description": "Banner image from the app's product listing, or `null` when none is uploaded.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppBannerImage"
							},
							{
								"type": "null"
							}
						]
					},
					"base_url": {
						"description": "The production base URL where the app is hosted. `null` if no base URL is configured, if the caller lacks the `developer:basic:read` permission on the app's account, or on list responses, which never expose it.",
						"type": ["string", "null"]
					},
					"businesses_created_count": {
						"description": "Number of businesses created from this app as a template.",
						"example": 0,
						"type": "integer"
					},
					"businesses_created_logo_urls": {
						"items": {
							"description": "Up to three logo image URLs from businesses created from this app as a template.",
							"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
							"type": "string"
						},
						"type": "array"
					},
					"creator": {
						"$ref": "#/components/schemas/AppCreator",
						"description": "The user who owns the publishing account."
					},
					"dashboard_path": {
						"description": "URL path for the account dashboard view, or `null` when not configured.",
						"example": "/dashboard/[companyId]",
						"type": ["string", "null"]
					},
					"description": {
						"description": "Short description shown in listings and search results, or `null` if none has been set.",
						"example": "Let members book a mobile detailing appointment without leaving your whop.",
						"type": ["string", "null"]
					},
					"discover_path": {
						"description": "URL path for the discover view, or `null` when not configured.",
						"example": "/discover",
						"type": ["string", "null"]
					},
					"domain_id": {
						"description": "Subdomain identifier for the app's proxied URL, forming https://{domain_id}.apps.whop.com.",
						"example": "whopappdomain0000001",
						"type": "string"
					},
					"domains": {
						"items": {
							"$ref": "#/components/schemas/AppDomain",
							"description": "Custom domain claims and assignments for this app, excluding removed domains. Empty when none exist; `null` when the caller lacks the account's `developer:basic:read` permission."
						},
						"type": ["array", "null"]
					},
					"experience_path": {
						"description": "URL path for the member-facing hub view, or `null` when not configured.",
						"example": "/experiences/[experienceId]",
						"type": ["string", "null"]
					},
					"hosted_url": {
						"description": "Full URL where the app's hosted web build is served, or `null` if no route is claimed.",
						"example": "https://shine-time-booking.whop.site",
						"type": ["string", "null"]
					},
					"icon": {
						"$ref": "#/components/schemas/AppIcon",
						"description": "The app's icon. Falls back to the default app icon when none is uploaded."
					},
					"id": {
						"description": "App ID, prefixed `app_`.",
						"example": "app_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"name": {
						"description": "Display name shown on the app store and in experience navigation.",
						"example": "Shine Time Booking",
						"type": "string"
					},
					"openapi_path": {
						"description": "URL path to the app's OpenAPI spec file, or `null` when not configured.",
						"example": "/openapi.json",
						"type": ["string", "null"]
					},
					"origin": {
						"description": "Full origin URL of the app's proxied domain, for example https://ab1c2d3e4f.apps.whop.com.",
						"example": "https://whopappdomain0000001.apps.whop.com",
						"type": ["string", "null"]
					},
					"previous_hosted_urls": {
						"items": {
							"description": "URLs the hosted web build was previously served at, most recent first. Populated when the route is changed; the old URL stops serving and is not redirected, but traffic recorded against it still belongs to this app.",
							"type": "string"
						},
						"type": "array"
					},
					"route": {
						"description": "Claimed subdomain route where hosted web builds are served (`myapp` for myapp.whop.site), or `null` if no route is claimed.",
						"example": "shine-time-booking",
						"type": ["string", "null"]
					},
					"skills_path": {
						"description": "URL path to the app's skills directory, or `null` when not configured.",
						"example": "/skills",
						"type": ["string", "null"]
					},
					"status": {
						"description": "Visibility on the Whop app store: `live` is publicly discoverable, `unlisted` is accessible only via direct link, `hidden` is not visible anywhere.",
						"enum": ["live", "unlisted", "hidden"],
						"example": "unlisted",
						"type": "string"
					},
					"verified": {
						"description": "Whether the app has been verified by Whop and is eligible for the featured apps section.",
						"example": false,
						"type": "boolean"
					}
				},
				"required": [
					"id",
					"name",
					"description",
					"status",
					"base_url",
					"domain_id",
					"route",
					"hosted_url",
					"previous_hosted_urls",
					"domains",
					"verified",
					"app_type",
					"origin",
					"experience_path",
					"discover_path",
					"dashboard_path",
					"skills_path",
					"openapi_path",
					"account",
					"icon",
					"banner_image",
					"creator",
					"businesses_created_count",
					"businesses_created_logo_urls"
				],
				"type": "object"
			},
			"App": {
				"properties": {
					"account": {
						"$ref": "#/components/schemas/AccountParent",
						"description": "The account that owns the app."
					},
					"api_key": {
						"description": "Legacy app API key used to authenticate requests on the app's behalf. `null` when no key exists or the caller lacks the `developer:manage_api_key` permission.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppApiKey"
							},
							{
								"type": "null"
							}
						]
					},
					"app_store_description": {
						"description": "Detailed description shown on the app store's in-depth app page, or `null` when none has been set.",
						"example": "Shine Time Booking turns a whop into a booking calendar. Members pick a package, choose a slot that fits the day's route, and pay up front. A reminder goes out the night before and the receipt lands as soon as the job is signed off.",
						"type": ["string", "null"]
					},
					"app_type": {
						"description": "The type of end-user the app is built for.",
						"enum": [
							"b2b_app",
							"b2c_app",
							"company_app",
							"component",
							"website"
						],
						"example": "b2c_app",
						"type": "string"
					},
					"banner_image": {
						"description": "Banner image from the app's product listing, or `null` when none is uploaded.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppBannerImage"
							},
							{
								"type": "null"
							}
						]
					},
					"base_url": {
						"description": "The production base URL where the app is hosted. `null` if no base URL is configured, if the caller lacks the `developer:basic:read` permission on the app's account, or on list responses, which never expose it.",
						"example": "https://booking.shinetime.example",
						"type": ["string", "null"]
					},
					"businesses_created_count": {
						"description": "Website businesses created from this app as a template.",
						"example": 5,
						"type": "integer"
					},
					"businesses_created_logo_urls": {
						"items": {
							"description": "Up to three logo image URLs from businesses created from this app as a template.",
							"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
							"type": "string"
						},
						"type": "array"
					},
					"creator": {
						"$ref": "#/components/schemas/AppCreator",
						"description": "The user who owns the publishing account."
					},
					"dashboard_path": {
						"description": "URL path for the account dashboard view, or `null` when not configured.",
						"example": "/dashboard/[companyId]",
						"type": ["string", "null"]
					},
					"default_api_key": {
						"description": "The app's default API key. `null` when the app has no default key or the caller lacks the `developer:manage_api_key` permission; `secret_key` is additionally `null` unless the caller could have created the key themselves.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppDefaultApiKey"
							},
							{
								"type": "null"
							}
						]
					},
					"deployment": {
						"description": "What the app has left to publish, and how a publish in flight is going — `status` is only ever `unpublished`, `publishing`, or `failed` here. `null` means there is nothing to report: the app is fully published, there is no working copy to publish from, or the caller cannot deploy this app. Tell those apart from the app's own `production_web_build`.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppDeployment"
							},
							{
								"type": "null"
							}
						]
					},
					"description": {
						"description": "Short description shown in listings and search results, or `null` if none has been set.",
						"example": "Let members book a mobile detailing appointment without leaving your whop.",
						"type": ["string", "null"]
					},
					"discover_path": {
						"description": "URL path for the discover view, or `null` when not configured.",
						"example": "/discover",
						"type": ["string", "null"]
					},
					"domain_id": {
						"description": "Subdomain identifier for the app's proxied URL, forming https://{domain_id}.apps.whop.com.",
						"example": "whopappdomain0000001",
						"type": "string"
					},
					"domains": {
						"items": {
							"$ref": "#/components/schemas/AppDomain",
							"description": "Custom domain claims and assignments for this app, excluding removed domains. Empty when none exist; `null` when the caller lacks the account's `developer:basic:read` permission."
						},
						"type": ["array", "null"]
					},
					"elements_used": {
						"items": {
							"description": "Whop Elements the app's production web build mounts, as `<namespace>.<element>` keys (sub-controller children take a third segment, e.g. `payments.cardFields.cardNumber`). A bare namespace means the build reaches that namespace but the individual elements could not be resolved. Empty when the build mounts none, when it has not been scanned yet, or when the app has no production web build.",
							"enum": [
								"ads",
								"ads.billing-setup",
								"ads.campaign-creator",
								"ads.reporting",
								"ads.reporting.chart",
								"ads.reporting.table",
								"checkout",
								"checkout.checkout",
								"checkout.expressCheckout",
								"payments",
								"payments.address",
								"payments.branding",
								"payments.card",
								"payments.cardFields",
								"payments.cardFields.cardCvc",
								"payments.cardFields.cardExpiry",
								"payments.cardFields.cardNumber",
								"payments.email",
								"payments.payment",
								"payments.taxId",
								"tracking",
								"tracking.events",
								"tracking.people",
								"wallet",
								"wallet.activity",
								"wallet.balances",
								"wallet.balances.balance",
								"wallet.balances.list",
								"wallet.cards",
								"wallet.deposit",
								"wallet.send",
								"wallet.withdraw",
								"websites",
								"websites.pixel-setup",
								"websites.websites"
							],
							"example": "ads",
							"type": "string"
						},
						"type": "array"
					},
					"experience_path": {
						"description": "URL path for the member-facing hub view, or `null` when not configured.",
						"example": "/experiences/[experienceId]",
						"type": ["string", "null"]
					},
					"hosted_url": {
						"description": "Full URL where the app's hosted web build is served, or `null` if no route is claimed.",
						"example": "https://shine-time-booking.whop.site",
						"type": ["string", "null"]
					},
					"icon": {
						"$ref": "#/components/schemas/AppIcon",
						"description": "The app's icon. Falls back to the default app icon when none is uploaded."
					},
					"id": {
						"description": "App ID, prefixed `app_`.",
						"example": "app_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"marketplace_status": {
						"description": "Approval status of the app's product listing on the Whop app store, or `null` when the app has no associated product.",
						"enum": [
							"not_available",
							"pending_review",
							"live_marketplace",
							null
						],
						"example": "not_available",
						"type": ["string", "null"]
					},
					"name": {
						"description": "Display name shown on the app store and in experience navigation.",
						"example": "Shine Time Booking",
						"type": "string"
					},
					"oauth_client_type": {
						"description": "How the app authenticates at the OAuth token endpoint.",
						"enum": ["public", "confidential"],
						"example": "public",
						"type": "string"
					},
					"openapi_path": {
						"description": "URL path to the app's OpenAPI spec file, or `null` when not configured.",
						"example": "/openapi.json",
						"type": ["string", "null"]
					},
					"origin": {
						"description": "Full origin URL of the app's proxied domain, for example https://ab1c2d3e4f.apps.whop.com.",
						"example": "https://whopappdomain0000001.apps.whop.com",
						"type": ["string", "null"]
					},
					"preview_token": {
						"description": "A short-lived signed pass scoping the caller to this app's gated preview hosts — every build preview and the live dev-server sandbox. Add it to a preview host as the `__whop_preview` query param (or `x-whop-preview-token` header). `null` unless the caller is a team member who can read the app's developer settings.",
						"example": "whop_preview_pass_example",
						"type": ["string", "null"]
					},
					"previous_hosted_urls": {
						"items": {
							"description": "URLs the hosted web build was previously served at, most recent first. Populated when the route is changed; the old URL stops serving and is not redirected, but traffic recorded against it still belongs to this app.",
							"example": "https://shine-time-booking.whop.site",
							"type": "string"
						},
						"type": "array"
					},
					"product_id": {
						"description": "ID of the app's product listing on the Whop app store, or `null` when the app has no associated product.",
						"example": "prod_xxxxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"production_android_build": {
						"description": "The approved build currently served on Android, or `null` when none is deployed.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppProductionBuild"
							},
							{
								"type": "null"
							}
						]
					},
					"production_ios_build": {
						"description": "The approved build currently served on iOS, or `null` when none is deployed.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppProductionBuild"
							},
							{
								"type": "null"
							}
						]
					},
					"production_web_build": {
						"description": "The approved build currently served on web, or `null` when none is deployed.",
						"oneOf": [
							{
								"$ref": "#/components/schemas/AppProductionBuild"
							},
							{
								"type": "null"
							}
						]
					},
					"redirect_uris": {
						"items": {
							"description": "Whitelisted OAuth callback URLs users are redirected to after authorizing the app.",
							"example": "https://booking.shinetime.example/oauth/callback",
							"type": "string"
						},
						"type": "array"
					},
					"requested_permissions": {
						"items": {
							"$ref": "#/components/schemas/AppRequestedPermission",
							"description": "Permissions the app requests on install."
						},
						"type": "array"
					},
					"required_scopes": {
						"items": {
							"description": "Scopes the app asks a user to grant when they authorize it, shown on the consent screen.",
							"enum": ["read_user"],
							"example": "read_user",
							"type": "string"
						},
						"type": "array"
					},
					"route": {
						"description": "Claimed subdomain route where hosted web builds are served (`myapp` for myapp.whop.site), or `null` if no route is claimed.",
						"example": "shine-time-booking",
						"type": ["string", "null"]
					},
					"secrets": {
						"description": "The app's production secrets as an object of string values, injected into the hosted server runtime. `null` when the caller lacks the `developer:update_app` permission.",
						"example": {
							"BOOKING_CALENDAR_ID": "cal_9f21"
						},
						"type": ["object", "null"]
					},
					"skills_path": {
						"description": "URL path to the app's skills directory, or `null` when not configured.",
						"example": "/skills",
						"type": ["string", "null"]
					},
					"status": {
						"description": "Visibility on the Whop app store: `live` is publicly discoverable, `unlisted` is accessible only via direct link, `hidden` is not visible anywhere.",
						"enum": ["live", "unlisted", "hidden"],
						"example": "unlisted",
						"type": "string"
					},
					"verified": {
						"description": "Whether the app has been verified by Whop and is eligible for the featured apps section.",
						"example": false,
						"type": "boolean"
					}
				},
				"required": [
					"id",
					"name",
					"description",
					"status",
					"base_url",
					"domain_id",
					"route",
					"hosted_url",
					"previous_hosted_urls",
					"domains",
					"verified",
					"app_type",
					"origin",
					"experience_path",
					"discover_path",
					"dashboard_path",
					"skills_path",
					"openapi_path",
					"account",
					"icon",
					"banner_image",
					"creator",
					"businesses_created_count",
					"businesses_created_logo_urls",
					"app_store_description",
					"requested_permissions",
					"api_key",
					"default_api_key",
					"production_web_build",
					"production_ios_build",
					"production_android_build",
					"redirect_uris",
					"required_scopes",
					"secrets",
					"product_id",
					"marketplace_status",
					"oauth_client_type",
					"deployment",
					"elements_used",
					"preview_token"
				],
				"type": "object"
			},
			"V1ErrorResponse": {
				"properties": {
					"error": {
						"properties": {
							"code": {
								"description": "Machine-readable reason for this specific refusal, such as `bank_warning_not_acknowledged`. Only present when the error carries one.",
								"type": "string"
							},
							"message": {
								"description": "Human-readable error message.",
								"example": "account_id is required",
								"type": "string"
							},
							"type": {
								"description": "Machine-readable error code.",
								"example": "bad_request",
								"type": "string"
							}
						},
						"required": ["type", "message"],
						"type": "object"
					}
				},
				"required": ["error"],
				"type": "object"
			},
			"AccountParent": {
				"properties": {
					"fees": {
						"additionalProperties": {
							"properties": {
								"fixed_fee_usd": {
									"description": "Fixed markup in US dollars per transaction.",
									"type": "number"
								},
								"percentage_fee": {
									"description": "Percentage of the transaction charged as markup.",
									"type": "number"
								}
							},
							"required": ["percentage_fee", "fixed_fee_usd"],
							"type": "object"
						},
						"description": "Markup rates this parent charges the connected account being read, keyed by fee type (for example `crypto_deposit_markup`), each with `percentage_fee` and `fixed_fee_usd`. Resolved with the connected account's own overrides winning over the platform default.",
						"example": {
							"crypto_swap_markup": {
								"fixed_fee_usd": 0,
								"percentage_fee": 3
							},
							"transfer_markup": {
								"fixed_fee_usd": 0,
								"percentage_fee": 0
							}
						},
						"type": "object"
					},
					"id": {
						"description": "Account ID, prefixed `biz_`.",
						"example": "biz_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"logo_url": {
						"description": "Account logo image URL.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"route": {
						"description": "Account public route identifier.",
						"example": "shine-time-holdings",
						"type": "string"
					},
					"title": {
						"description": "Account display name.",
						"example": "Shine Time Holdings",
						"type": "string"
					}
				},
				"required": ["id", "title", "route", "logo_url"],
				"type": "object"
			},
			"AppBannerImage": {
				"properties": {
					"url": {
						"description": "Banner image URL, taken from the app's product listing.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": "string"
					}
				},
				"required": ["url"],
				"type": "object"
			},
			"AppCreator": {
				"properties": {
					"id": {
						"description": "User ID, prefixed `user_`.",
						"example": "user_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"name": {
						"description": "Display name.",
						"example": "Marcus Webb",
						"type": ["string", "null"]
					},
					"username": {
						"description": "Public username.",
						"example": "marcuswebb",
						"type": "string"
					}
				},
				"required": ["id", "name", "username"],
				"type": "object"
			},
			"AppDomain": {
				"properties": {
					"domain": {
						"description": "Normalized hostname assigned to this app.",
						"type": "string"
					},
					"id": {
						"description": "Domain ID, prefixed `dom_`.",
						"type": "string"
					},
					"status": {
						"description": "Domain lifecycle status, matching the domain resource.",
						"enum": [
							"pending_verification",
							"provisioning",
							"active",
							"action_required",
							"deleting",
							"removed"
						],
						"example": "pending_verification",
						"type": "string"
					}
				},
				"required": ["id", "domain", "status"],
				"type": "object"
			},
			"AppIcon": {
				"properties": {
					"url": {
						"description": "Icon image URL. Always present — the default app icon when none is uploaded.",
						"example": "https://pub-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx.r2.dev/custom-perk-icon.svg",
						"type": "string"
					}
				},
				"required": ["url"],
				"type": "object"
			},
			"AppApiKey": {
				"properties": {
					"created_at": {
						"description": "When the key was created, as an ISO 8601 timestamp.",
						"example": "2026-01-01T12:00:00.000Z",
						"type": "string"
					},
					"token": {
						"description": "The key's secret token, sent as a bearer token to authenticate requests on the app's behalf.",
						"example": "eyJhbGciOiJFUzI1NiJ9.xxxxxxxxxxxxxxxxxxxxxxxx.xxxxxxxxxxxxxxxxxxxxxxxx",
						"type": "string"
					}
				},
				"required": ["token", "created_at"],
				"type": "object"
			},
			"AppDefaultApiKey": {
				"properties": {
					"id": {
						"description": "API key ID, prefixed `apik_`.",
						"example": "apik_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"name": {
						"description": "Human-readable name identifying the API key, or `null` when none was set.",
						"example": "Default",
						"type": ["string", "null"]
					},
					"obfuscated_secret_key": {
						"description": "Masked version of the secret key, so the key can be recognized without revealing the full secret.",
						"example": "apik_xxxx....xxxx",
						"type": "string"
					},
					"secret_key": {
						"description": "The full secret used to authenticate requests. `null` unless the caller could have created the key themselves.",
						"example": "apik_xxxxxxxxxxxxxx_C0000_C_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
						"type": ["string", "null"]
					}
				},
				"required": ["id", "name", "obfuscated_secret_key", "secret_key"],
				"type": "object"
			},
			"AppDeployment": {
				"properties": {
					"app_id": {
						"description": "The app being deployed, prefixed `app_`.",
						"example": "app_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"build_id": {
						"description": "The build the deployment produced, prefixed `abld_`, or `null` until it succeeds.",
						"type": ["string", "null"]
					},
					"draft": {
						"description": "Whether the running or last deployment uploaded a build without making it live.",
						"example": false,
						"type": "boolean"
					},
					"error": {
						"description": "Why the deployment failed, or `null` when it did not.",
						"type": ["string", "null"]
					},
					"estimated_duration_ms": {
						"description": "How long this deployment is expected to take in total, estimated from previous runs.",
						"example": 150000,
						"type": ["integer", "null"]
					},
					"estimated_remaining_ms": {
						"description": "How much longer the deployment is expected to take. Held above zero until it actually finishes.",
						"example": 91000,
						"type": ["integer", "null"]
					},
					"finished_at": {
						"description": "When the deployment ended, in milliseconds since the epoch, or `null` while it is still running.",
						"type": ["integer", "null"]
					},
					"phase": {
						"description": "The stage a running deployment has reached, or `null` when none is running. Later phases dominate the wall clock: `process_archive` waits on the upload pipeline and `promote` waits for the build to go live.",
						"enum": [
							"install",
							"build",
							"typecheck",
							"upload_build",
							"upload_source",
							"process_archive",
							"create_build",
							"promote",
							null
						],
						"example": "process_archive",
						"type": ["string", "null"]
					},
					"progress": {
						"description": "Fraction of the deployment estimated to be complete, from 0 to 1. Stops just short of 1 until the run ends.",
						"example": 0.39,
						"type": ["number", "null"]
					},
					"started_at": {
						"description": "When the deployment began, in milliseconds since the epoch, or `null` when none has run.",
						"example": 1767268800000,
						"type": ["integer", "null"]
					},
					"status": {
						"description": "Whether the app has anything to publish, and what a publish in flight is doing. `unpublished` means publishing would ship something new; `no_source` means the sandbox holds no copy of this app, so there is nothing to publish from.",
						"enum": [
							"published",
							"unpublished",
							"publishing",
							"failed",
							"no_source"
						],
						"example": "publishing",
						"type": "string"
					},
					"url": {
						"description": "Where the deployed site is served, or `null` unless the deployment went live.",
						"type": ["string", "null"]
					}
				},
				"required": [
					"app_id",
					"status",
					"draft",
					"phase",
					"started_at",
					"finished_at",
					"estimated_duration_ms",
					"estimated_remaining_ms",
					"progress",
					"build_id",
					"url",
					"error"
				],
				"type": "object"
			},
			"AppProductionBuild": {
				"properties": {
					"checksum": {
						"description": "Client-generated checksum of the build file, used to verify file integrity.",
						"example": "xxxxxxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"file_url": {
						"description": "URL to download the uploaded build artifact.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"id": {
						"description": "App build ID, prefixed `abld_`.",
						"example": "apbu_xxxxxxxxxxxxxx",
						"type": "string"
					},
					"source_url": {
						"description": "URL to download the compressed source code archive that produced this build, or `null` when the build was uploaded without a source archive.",
						"example": "https://whop-assets-example.s3.amazonaws.com/uploads/image/2026-01-01/xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
						"type": ["string", "null"]
					},
					"status": {
						"description": "The build's review status.",
						"enum": ["draft", "pending", "approved", "rejected"],
						"example": "approved",
						"type": "string"
					}
				},
				"required": ["id", "file_url", "source_url", "checksum", "status"],
				"type": "object"
			},
			"AppRequestedPermission": {
				"properties": {
					"is_required": {
						"description": "Whether the app requires the permission to be granted on install, as opposed to requesting it optionally.",
						"example": true,
						"type": "boolean"
					},
					"justification": {
						"description": "The developer's explanation of why the app needs the permission, or `null` when none was provided.",
						"example": "Creates apps on behalf of the installing company.",
						"type": ["string", "null"]
					},
					"permission_action": {
						"$ref": "#/components/schemas/AppRequestedPermissionAction",
						"description": "The permission action the app requests."
					}
				},
				"required": ["permission_action", "is_required", "justification"],
				"type": "object"
			},
			"AppRequestedPermissionAction": {
				"properties": {
					"action": {
						"description": "The permission action's identifier, for example `company:basic:read`.",
						"example": "company:basic:read",
						"type": "string"
					},
					"name": {
						"description": "Human-readable name of the action.",
						"example": "company:basic:read",
						"type": "string"
					}
				},
				"required": ["action", "name"],
				"type": "object"
			}
		},
		"parameters": {
			"ApiVersionDate": {
				"description": "Pins the request to a dated API version.",
				"in": "header",
				"name": "Api-Version-Date",
				"required": false,
				"schema": {
					"example": "2026-09-15",
					"type": "string"
				}
			},
			"IdempotencyKey": {
				"description": "A unique key that makes this request safe to retry. See [Idempotent requests](https://docs.whop.com/developer/api/idempotency).",
				"in": "header",
				"name": "Idempotency-Key",
				"required": false,
				"schema": {
					"example": "d9105228-4a08-46b1-8b91-42fed586d383",
					"maxLength": 255,
					"type": "string"
				}
			}
		},
		"responses": {
			"InvalidParameters": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Invalid Parameters"
			},
			"Unauthorized": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Unauthorized"
			},
			"Forbidden": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Forbidden"
			},
			"NotFound": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Resource not found"
			},
			"Conflict": {
				"content": {
					"application/json": {
						"schema": {
							"$ref": "#/components/schemas/V1ErrorResponse"
						}
					}
				},
				"description": "Conflict"
			}
		}
	}
}
